import { getRequest } from "api/commonActions/get";
import { postRequest } from "api/commonActions/post";
import { patchRequest } from "api/commonActions/update";
import { Requests } from "config/api";
import { UserType } from "types/loadedData";

export type ValuationResult = {
  likelySalePrice: number;
  lowPrice: number;
  highPrice: number;
  quickSalePrice: number;
  suggestedAskingPrice: number;
  confidence: "Low" | "Medium" | "High";
  comparableCount: number;
  effectiveComparableCount: number;
  askingPriceCount: number;
  completedSaleCount: number;
  externalListingCount: number;
  verifiedSaleCount: number;
  saleRatio: number;
  calibrationSource: "verified_sales" | "completed_sales" | "provisional_market_rule";
  halfLifeDays: number;
  adjustmentProfile?: {
    yearRate: number;
    mileageRatePer10k: number;
    yearPairCount: number;
    mileagePairCount: number;
    source: "local_market_blend" | "conservative_prior";
  };
  sourceCount?: number;
  largestSourceShare?: number;
  relativeSpread?: number;
  dataQualityScore?: number;
  detailMatch?: {
    evidenceTier: "Exact" | "Adjusted" | "Insufficient";
    rawComparableCount: number;
    compatibleComparableCount: number;
    exactDetailComparableCount: number;
    rejectedComparableCount: number;
    usesAdjustedYear: boolean;
    missingTargetDetails: string[];
    filtersApplied: string[];
  };
  confidenceReasons?: string[];
  needsHumanReview: boolean;
  methodVersion: string;
  generatedAt: string;
};

export type CarValuation = {
  _id: string;
  requester?: Pick<UserType, "_id" | "name" | "phoneNumber" | "email">;
  contactPhone?: string;
  makeName: string;
  model: string;
  variant?: string;
  year: number;
  mileage?: number;
  fuelType?: string;
  transmissionType?: string;
  location?: string;
  condition: string;
  accidentHistory: string;
  repaintedPanels?: number;
  estimatedRepairCost?: number;
  result: ValuationResult;
  reviewStatus: "AutoEstimated" | "NeedsReview" | "Reviewed" | "Closed";
  staffLikelySalePrice?: number;
  staffLowPrice?: number;
  staffHighPrice?: number;
  staffNotes?: string;
  actualSalePrice?: number;
  actualSoldAt?: string;
  originalAskingPrice?: number;
  outcomeVerified?: boolean;
  createdAt: string;
};

export type PriceObservation = {
  _id: string;
  sourceType: string;
  sourceProvider: string;
  makeName: string;
  model: string;
  year: number;
  mileage?: number;
  fuelType?: string;
  askingPrice?: number;
  finalSalePrice?: number;
  verified: boolean;
  status: string;
  lastObservedAt: string;
};

export type CanonicalVehicleModel = {
  _id: string;
  make?: { _id: string; name: string };
  makeName: string;
  name: string;
  normalizedName: string;
  signature: string;
  status: "Pending" | "Approved" | "Rejected";
  aliases: Array<{ value: string; normalizedValue: string; source: string }>;
  suggestedCanonicalModel?: { _id: string; name: string; normalizedName: string };
  suggestedConfidence: number;
  suggestedMethod?: string;
  lastSeenAt: string;
  evidence?: ModelEvidence;
  reviewCandidates?: ModelReviewCandidate[];
};

export type PriceTrendPoint = {
  date: string;
  askingMedian?: number;
  verifiedSaleMedian?: number;
  askingCount: number;
  verifiedSaleCount: number;
  sourceCount: number;
};

export type PriceTrendResult = {
  points: PriceTrendPoint[];
  summary: {
    observationCount: number;
    sourceCount: number;
    firstAskingMedian: number;
    latestAskingMedian: number;
    askingChangePercent: number;
    from?: string;
    to?: string;
  };
  availableSources: string[];
  requiresVehicleSearch: boolean;
};

export type JijiPriceObservationPreview = {
  sourceProvider: "Jiji";
  sourceId: string;
  sourceUrl: string;
  title: string;
  makeName: string;
  model: string;
  year: number;
  mileage?: number;
  fuelType?: string;
  transmissionType?: string;
  color?: string;
  location: string;
  askingPrice: number;
  usageType: "used";
  listingType: "Sale";
  status: "Active";
  observedAt: string;
  conditionLabel?: string;
};

export type JijiCollectionResult = {
  index: number;
  sourceUrl: string;
  status: "imported" | "updated" | "rejected" | "duplicate";
  vehicle?: string;
  askingPrice?: number;
  missingDetails?: string[];
  message?: string;
};

export type ModelEvidence = {
  observationCount: number;
  medianPrice: number;
  minYear?: number;
  maxYear?: number;
  fuels: string[];
  sources: string[];
  verifiedSaleCount: number;
};

export type ModelReviewCandidate = {
  model: {
    _id: string;
    name: string;
    normalizedName: string;
    status: "Pending" | "Approved";
  };
  textScore: number;
  method: string;
  recommended: boolean;
  warnings: string[];
  evidence: ModelEvidence;
  priceRatio: number;
};

export type ValuationCalibration = {
  count: number;
  verifiedCount: number;
  medianAbsolutePercentageError: number;
  meanAbsolutePercentageError: number;
  medianBiasPercentage: number;
  within10Percent: number;
  within15Percent: number;
};

export type TemporalValuationBacktest = {
  eligibleOutcomeCount: number;
  evaluatedCount: number;
  skippedNoPriorEvidence: number;
  metrics: ValuationCalibration;
  globallyReadyForAutomaticPricing?: boolean;
  readyModelCount?: number;
  modelScorecards?: Array<{
    canonicalModel: string;
    makeName: string;
    model: string;
    metrics: ValuationCalibration;
    readyForAutomaticPricing: boolean;
  }>;
};

export type ValuationEvidenceCoverage = {
  total: number;
  accepted: number;
  pending: number;
  verifiedSales: number;
  sourceCount: number;
};

export type MissingSaleOutcome = {
  _id: string;
  tag?: string;
  model: string;
  year: number;
  price?: number;
  phone?: string;
  owner?: Pick<UserType, "_id" | "name" | "phoneNumber">;
  make?: { _id: string; name: string };
  postedAt?: string;
  soldAt?: string;
};

export const GetValuations = async (query: {
  search?: string;
  status?: string;
  limit?: number;
  offset?: number;
} = {}) => {
  const response = await getRequest({ route: Requests.get.getValuations, queryParams: query });
  return {
    status: response?.status,
    message: response?.data?.msg,
    valuations: (response?.data?.valuations || []) as CarValuation[],
    count: Number(response?.data?.count || 0),
  };
};

export const ReviewValuation = async (id: string, payload: Record<string, unknown>) => {
  const response = await patchRequest({
    route: Requests.update.reviewValuation,
    pathVariables: { id },
    payload,
  });
  return { status: response?.status, message: response?.data?.msg, valuation: response?.data?.valuation as CarValuation };
};

export const RecordValuationOutcome = async (id: string, payload: Record<string, unknown>) => {
  const response = await patchRequest({
    route: Requests.update.recordValuationOutcome,
    pathVariables: { id },
    payload,
  });
  return { status: response?.status, message: response?.data?.msg, valuation: response?.data?.valuation as CarValuation };
};

export const GetValuationObservations = async (query: { search?: string; sourceType?: string } = {}) => {
  const response = await getRequest({
    route: Requests.get.getValuationObservations,
    queryParams: query,
  });
  return {
    status: response?.status,
    message: response?.data?.msg,
    observations: (response?.data?.observations || []) as PriceObservation[],
    count: Number(response?.data?.count || 0),
  };
};

export const GetValuationPriceTrend = async (query: {
  search: string;
  sourceProvider?: string;
  days?: number;
}) => {
  const response = await getRequest({
    route: Requests.get.getValuationPriceTrend,
    queryParams: query,
  });
  return {
    status: response?.status,
    message: response?.data?.msg,
    trend: {
      points: response?.data?.points || [],
      summary: response?.data?.summary || {
        observationCount: 0,
        sourceCount: 0,
        firstAskingMedian: 0,
        latestAskingMedian: 0,
        askingChangePercent: 0,
      },
      availableSources: response?.data?.availableSources || [],
      requiresVehicleSearch: Boolean(response?.data?.requiresVehicleSearch),
    } as PriceTrendResult,
  };
};

export const PreviewJijiValuationObservation = async (sourceUrl: string) => {
  const response = await postRequest({
    route: Requests.post.previewJijiValuationObservation,
    payload: { sourceUrl },
  });
  return {
    status: response?.status,
    message: response?.data?.msg,
    record: response?.data?.record as JijiPriceObservationPreview,
  };
};

export const CollectJijiValuationObservations = async (urls: string[]) => {
  const response = await postRequest({
    route: Requests.post.collectJijiValuationObservations,
    payload: { urls },
  });
  return {
    status: response?.status,
    message: response?.data?.msg,
    summary: response?.data?.summary as {
      received: number;
      imported: number;
      updated: number;
      rejected: number;
      duplicates: number;
    },
    results: (response?.data?.results || []) as JijiCollectionResult[],
  };
};

export const CreateValuationObservation = async (payload: Record<string, unknown>) => {
  const response = await postRequest({
    route: Requests.post.createValuationObservation,
    payload,
  });
  return { status: response?.status, message: response?.data?.msg, observation: response?.data?.observation as PriceObservation };
};

export const GetCanonicalVehicleModels = async (query: {
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
} = {}) => {
  const response = await getRequest({
    route: Requests.get.getCanonicalVehicleModels,
    queryParams: query,
  });
  return {
    status: response?.status,
    message: response?.data?.msg,
    models: (response?.data?.models || []) as CanonicalVehicleModel[],
    count: Number(response?.data?.count || 0),
  };
};

export const ResolveCanonicalVehicleModel = async (
  id: string,
  payload: { action: "approve" | "merge" | "reject"; targetModel?: string }
) => {
  const response = await patchRequest({
    route: Requests.update.resolveCanonicalVehicleModel,
    pathVariables: { id },
    payload,
  });
  return {
    status: response?.status,
    message: response?.data?.msg,
    model: response?.data?.model as CanonicalVehicleModel,
  };
};

export const GetValuationCalibration = async () => {
  const response = await getRequest({ route: Requests.get.getValuationCalibration });
  return {
    status: response?.status,
    message: response?.data?.msg,
    calibration: response?.data?.calibration as ValuationCalibration,
    temporalBacktest: response?.data?.temporalBacktest as TemporalValuationBacktest,
    evidenceCoverage: response?.data?.evidenceCoverage as ValuationEvidenceCoverage,
    missingSaleOutcomes: (response?.data?.missingSaleOutcomes || []) as MissingSaleOutcome[],
    readyForAutomaticPricing: Boolean(response?.data?.readyForAutomaticPricing),
  };
};

export const ImportValuationObservations = async (payload: {
  sourceProvider: string;
  rightsConfirmed: boolean;
  records: Array<Record<string, unknown>>;
}) => {
  const response = await postRequest({
    route: Requests.post.importValuationObservations,
    payload,
  });
  return {
    status: response?.status,
    message: response?.data?.msg,
    summary: response?.data?.summary as {
      received: number;
      imported: number;
      updated: number;
      rejected: number;
    },
    errors: (response?.data?.errors || []) as Array<{ index: number; message: string }>,
  };
};
